import React, { CSSProperties, ReactNode } from "react";
import { Box } from "@material-ui/core";
import { format } from "date-fns";
import HistoryEntry from "./HistoryEntry.model";
import StyledTooltip from "../../../../components/Wrapper/StyledTooltip";
import StatusChip, { TestRequestDisplayedStateStatus } from "../../../../components/Chip/StatusChip";
import formatIdentifierAsString from "../../../../api/services/common/formatIdentifierAsString";
import LaboratoryResponse from "../../../../api/models/LaboratoryResponse";

type TestRequestLaboratoryChange = Omit<
  TestRequestRaw.Schemas.TestRequestLaboratoryChange,
  "laboratory" | "previousLaboratory"
> & {
  laboratory: LaboratoryResponse | null;
  previousLaboratory: LaboratoryResponse | null;
};
type TestRequestHistoryEntry =
  | Exclude<TestRequestRaw.Schemas.TestRequestHistoryEntry, "TestRequestLaboratoryChange">
  | TestRequestLaboratoryChange;
type TestRequestStatusChange = TestRequestRaw.Schemas.TestRequestStatusChange;
type TestRequestAssigneeChange = TestRequestRaw.Schemas.TestRequestAssigneeChange;
type TestSeriesStatusChange = TestRequestRaw.Schemas.TestSeriesStatusChange;

const textEllipsis: CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
};

function mapLab(labs: LaboratoryResponse[]): ReactNode {
  if (labs.length === 0) {
    return "";
  }
  if (labs.length === 1) {
    return labs[0].name;
  }
  return (
    <StyledTooltip
      title={labs
        .map((lab) => lab.name)
        .map((labName) => (
          <Box>{labName}</Box>
        ))}
    >
      <Box style={textEllipsis}>Multiple</Box>
    </StyledTooltip>
  );
}

function mapDate(time: string): string {
  return format(new Date(time), "yyyy/MM/dd kk:mm");
}

function mapStatusChangeAction(historyEntry: TestRequestStatusChange) {
  const oldState = formatIdentifierAsString(historyEntry.oldState);
  const newState = formatIdentifierAsString(historyEntry.newState);
  if (historyEntry.oldState === "__NONE") {
    return {
      text: "",
      suffix: "Request creation",
    };
  }
  return {
    text: "Status change",
    suffix: `from ${oldState} to ${newState}`,
  };
}

function mapAssigneeChangeAction(historyEntry: TestRequestAssigneeChange) {
  if (!historyEntry.oldAssignee?.userName) {
    return {
      text: "Assignee change",
      suffix: `to ${historyEntry.newAssignee.userName}`,
    };
  }
  return {
    text: "Assignee change",
    suffix: `from ${historyEntry.oldAssignee.userName} to ${historyEntry.newAssignee.userName}`,
  };
}

function mapLabChangeAction(historyEntry: TestRequestLaboratoryChange) {
  const oldLab = historyEntry.previousLaboratory?.name;
  const newLab = historyEntry.laboratory?.name;
  if (!historyEntry.previousLaboratory) {
    // lab "_UNDEFINED"
    return {
      text: "Lab change",
      suffix: `to ${newLab}`,
    };
  }
  return {
    text: "Lab change",
    suffix: `from ${oldLab} to ${newLab}`,
  };
}

function mapTestSeriesStatusChangeAction(historyEntry: TestSeriesStatusChange) {
  const testSeries = `${historyEntry.testSeriesNumber} - ${historyEntry.testSeriesName}`;
  if (!historyEntry.oldStatus) {
    return {
      text: "Test series creation:",
      suffix: testSeries,
    };
  }
  return {
    text: "Test series status change",
    suffix: `from ${formatIdentifierAsString(historyEntry.oldStatus)} to ${formatIdentifierAsString(
      historyEntry.newStatus,
    )}: ${testSeries}`,
  };
}

function mapAction(historyEntry: TestRequestHistoryEntry): {
  text: string;
  suffix: string;
} {
  if (historyEntry.type === "TEST_REQUEST_STATUS_CHANGE") {
    return mapStatusChangeAction(historyEntry as TestRequestStatusChange);
  }
  if (historyEntry.type === "TEST_REQUEST_ASSIGNEE_CHANGE") {
    return mapAssigneeChangeAction(historyEntry as TestRequestAssigneeChange);
  }
  if (historyEntry.type === "TEST_REQUEST_LABORATORY_CHANGE") {
    return mapLabChangeAction(historyEntry as TestRequestLaboratoryChange);
  }
  if (historyEntry.type === "TEST_SERIES_STATUS_CHANGE") {
    return mapTestSeriesStatusChangeAction(historyEntry as TestSeriesStatusChange);
  }
  return {
    text: "Unknown action",
    suffix: "",
  };
}

export default function mapHistoryEntry(historyEntry: TestRequestHistoryEntry): HistoryEntry {
  const action = mapAction(historyEntry);
  return {
    date: mapDate(historyEntry.updateDateTime),
    action: (
      <StyledTooltip
        title={
          <>
            <b>{action.text}</b> {action.suffix}
          </>
        }
      >
        <Box style={textEllipsis}>
          <b>{action.text}</b> {action.suffix}
        </Box>
      </StyledTooltip>
    ),
    testRequestState: <StatusChip status={historyEntry.testRequestStatus as TestRequestDisplayedStateStatus} />,
    actor: historyEntry.actor.userName || "Unknown User",
    lab: mapLab(historyEntry.actor.assignedLaboratories),
  };
}
